import {
  liquidGlassPillShadowSx,
  liquidGlassPressTransition,
  liquidGlassSurfaceFillSx,
  LIQUID_GLASS_CIRCLE_PRESS_SCALE,
  MainActionButton,
  MINI_BUTTON_ACTION_SIZE,
  MINI_BUTTON_DOCK_GAP,
  miniButtonDockBottomInset,
  SecondaryActionButton,
  useLiquidGlassPress,
} from '@focuslee/shared-mini-app';
import { Box, Stack } from '@mui/material';
import { motion } from 'framer-motion';
import { type FC, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { CommonIcon } from '../common/CommonIcon';
import { LiquidGlassCircleButton } from '../common/LiquidGlassCircleButton';
import { SegmentedChipBar, type SegmentedChipItem } from '../common/SegmentedChipBar';

const bottomUiInset = `calc(${MINI_BUTTON_DOCK_GAP}px + ${miniButtonDockBottomInset})`;

type TemplatesPageDockProps = {
  onBack: () => void;
  onSelect: () => void;
  selectEnabled: boolean;
  searchOpen: boolean;
  searchQuery: string;
  onSearchQueryChange: (value: string) => void;
  onOpenSearch: () => void;
  onCloseSearch: () => void;
  categoryChips?: SegmentedChipItem[];
  activeCategoryId?: string;
  onCategoryChange?: (id: string) => void;
};

export const TemplatesPageDock: FC<TemplatesPageDockProps> = ({
  onBack,
  onSelect,
  selectEnabled,
  searchOpen,
  searchQuery,
  onSearchQueryChange,
  onOpenSearch,
  onCloseSearch,
  categoryChips = [],
  activeCategoryId = '',
  onCategoryChange,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const cancelPress = useLiquidGlassPress({ onActivate: onCloseSearch });

  return (
    <Box
      sx={{
        flexShrink: 0,
        width: '100%',
        boxSizing: 'border-box',
        pb: bottomUiInset,
        pt: `${MINI_BUTTON_DOCK_GAP}px`,
        pointerEvents: 'none',
        '& button, & [role="button"]': { pointerEvents: 'auto' },
      }}
    >
      {categoryChips.length > 1 ? (
        <Box
          sx={{
            px: `${MINI_BUTTON_DOCK_GAP}px`,
            pb: 1,
            pointerEvents: 'auto',
          }}
        >
          <SegmentedChipBar
            items={categoryChips}
            activeId={activeCategoryId}
            onChange={id => onCategoryChange?.(id)}
            preserveFocusOnClick={searchOpen}
          />
        </Box>
      ) : null}

      {searchOpen ? (
        <Box sx={{ px: `${MINI_BUTTON_DOCK_GAP}px`, pb: 1, pointerEvents: 'auto' }}>
          <Box
            data-testid="template-search-bar"
            sx={{
              position: 'relative',
              height: MINI_BUTTON_ACTION_SIZE,
              borderRadius: '999px',
              overflow: 'visible',
              ...liquidGlassPillShadowSx,
            }}
          >
            <Box
              data-testid="template-search-surface"
              sx={{
                height: '100%',
                pointerEvents: 'auto',
                borderRadius: 'inherit',
                overflow: 'hidden',
                display: 'flex',
                alignItems: 'center',
                ...liquidGlassSurfaceFillSx,
              }}
              onPointerDown={event => {
                if (!(event.target as HTMLElement).closest('button')) inputRef.current?.focus();
              }}
            >
              <CommonIcon
                icon="search"
                type="filled"
                size={18}
                color="var(--tg-theme-text-color)"
                style={{ flexShrink: 0, marginLeft: 14, marginRight: 8 }}
              />
              <Box
                component="input"
                ref={inputRef}
                type="text"
                inputMode="search"
                enterKeyHint="search"
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
                autoFocus
                placeholder={t('templates.searchPlaceholder')}
                value={searchQuery}
                onChange={e => onSearchQueryChange(e.target.value)}
                sx={{
                  flex: 1,
                  minWidth: 0,
                  height: MINI_BUTTON_ACTION_SIZE,
                  border: 'none',
                  outline: 'none',
                  bgcolor: 'transparent',
                  color: 'var(--tg-theme-text-color)',
                  fontSize: 16,
                  lineHeight: `${MINI_BUTTON_ACTION_SIZE}px`,
                  px: 0,
                  py: 0,
                  margin: 0,
                  boxSizing: 'border-box',
                  appearance: 'none',
                  WebkitAppearance: 'none',
                  '&::placeholder': { color: 'var(--tg-theme-hint-color)', opacity: 1 },
                }}
              />
              <Box
                component={motion.div}
                initial={false}
                animate={{ scale: cancelPress.pressed ? LIQUID_GLASS_CIRCLE_PRESS_SCALE : 1 }}
                transition={liquidGlassPressTransition}
                sx={{ flexShrink: 0, mr: '4px' }}
              >
                <Box
                  component="button"
                  type="button"
                  aria-label={t('templates.closeSearch')}
                  onPointerDown={cancelPress.onPointerDown}
                  onPointerMove={cancelPress.onPointerMove}
                  onPointerUp={cancelPress.onPointerUp}
                  onPointerCancel={cancelPress.onPointerCancel}
                  onClick={cancelPress.onClick}
                  sx={{
                    width: MINI_BUTTON_ACTION_SIZE - 8,
                    height: MINI_BUTTON_ACTION_SIZE - 8,
                    border: 'none',
                    bgcolor: 'transparent',
                    color: 'var(--tg-theme-text-color)',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    WebkitTapHighlightColor: 'transparent',
                    outline: 'none',
                    '&:focus-visible': { outline: '2px solid var(--tg-theme-link-color)', outlineOffset: 2 },
                  }}
                >
                  <CommonIcon icon="close" type="filled" size={20} color="var(--tg-theme-text-color)" />
                </Box>
              </Box>
            </Box>
          </Box>
        </Box>
      ) : null}

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: `${MINI_BUTTON_DOCK_GAP}px`,
          width: '100%',
          boxSizing: 'border-box',
          px: `${MINI_BUTTON_DOCK_GAP}px`,
          minHeight: MINI_BUTTON_ACTION_SIZE,
        }}
      >
        <SecondaryActionButton text={t('templates.back')} onClick={onBack} navigation="back" />
        <Stack direction="row" alignItems="center" gap={`${MINI_BUTTON_DOCK_GAP}px`}>
          {!searchOpen && (
            <LiquidGlassCircleButton
              icon="search"
              ariaLabel={t('templates.search')}
              onClick={onOpenSearch}
              size={MINI_BUTTON_ACTION_SIZE}
              iconSize={24}
            />
          )}
          <MainActionButton text={t('templates.select')} onClick={onSelect} enabled={selectEnabled} />
        </Stack>
      </Box>
    </Box>
  );
};
